import * as Blockly from 'blockly/core'

import {
  BehaviorContext,
  BehaviorHandler,
  HudStyle,
  MissionBehaviors,
  MissionBehaviorsFactory,
} from '../behaviors'
import {
  AVATAR_ID,
  EditablePreviewWorld,
  PreviewRuntime,
  SceneDescriptor,
} from '../types'
import { ExecutionContext, Interpreter } from './interpreter'
import {
  ObjectHandlers,
  buildHandlers,
  buildIdToName,
  buildNameToId,
} from './objectHandlers'
import { MAX_DYNAMIC_PROGRAM_STARTS, RunSession } from './runSession'
import { isAabbTouching, matchesTouchTargetName } from './touchGeometry'

/** SceneRuntime の生成オプション。 */
export interface RuntimeOptions {
  /**
   * looks_showMessage（Lua: print）の出力先。Roblox Studio の Output
   * ウィンドウ相当の UI（PreviewPanel の出力ログ）へ流す。
   */
  onOutput?: (text: string) => void
  /**
   * sound_play（Lua: script.Parent.Sound:Play()）の再生通知。実際の音声再生は
   * PreviewPanel が objectId → 音源 URL を引いて行う。
   */
  onPlaySound?: (objectId: string) => void
  /**
   * ミッション GUI（Roblox の ScreenGui/TextLabel 相当）の HUD テキスト。
   * 環境ビヘイビア（タイマー・カウント等）が設定し、PreviewPanel が
   * ビューポート上部へ表示する。null で非表示。stop() で自動的にクリアする。
   */
  onHudText?: (text: string | null, style?: HudStyle, slot?: string) => void
  /**
   * ミッション進行に合わせた BGM の差し替え通知（Roblox の Sound:Stop() →
   * 別 Sound:Play() 相当）。環境ビヘイビアが setBgm で発火し、実際の音声
   * 再生は PreviewPanel が行う（ループ・ミュート・停止との連動も含む）。
   */
  onBgm?: (url: string) => void
}

/**
 * クリックを受け付ける最大距離（studs）。Roblox の ClickDetector
 * MaxActivationDistance 既定値 32 に合わせる。離れたままクリックで操作
 * できるとゲームルールが破綻するミッションがあるため、アバターが対象へ
 * 近づいたときだけ Blockly の onClicked を発火する。
 * 環境ビヘイビア（大砲等）は rbxl 由来の固有距離を各自で判定する。
 */
const CLICK_MAX_ACTIVATION_DISTANCE = 32

/**
 * onTouchObject の接触判定の間隔（ミリ秒）。物理エンジンの接触イベントは
 * 固定 body 同士で発生しないため、ランタイムが World 状態の AABB 重なりを
 * 周期チェックして Touched を近似する（enter エッジでのみ発火）。
 */
const TOUCH_OBJECT_CHECK_INTERVAL_MS = 50

/**
 * メッセージ送信の最大連鎖深さ。
 * onMessage が sendMessage を呼ぶ無限ループを防ぐためのガード。
 */
const MAX_MESSAGE_DEPTH = 50

/**
 * おためしプレイ実行ランタイムの標準実装。
 *
 * 責務:
 * - 各オブジェクトの workspace を解析して
 *   onPlay / onMessage / onClicked / onTouchCharacter を登録。
 * - start() で全 onPlay を協調的に並行起動（各 self = 自分のオブジェクト）。
 * - メッセージは全オブジェクト横断で配送（events_sendMessage → onMessage）。
 * - emitClick / emitTouch で当該オブジェクトの onClicked / onTouchCharacter を発火。
 * - stop() で進行中の wait / ループを AbortController で中断。
 * - control_destroy で当該オブジェクトの全ハンドラを個別に中断。
 */
class SceneRuntime implements PreviewRuntime {
  private readonly handlers: ReadonlyArray<ObjectHandlers>
  private readonly objectIds: ReadonlyArray<string>
  /** 名前 → workspace。クローンへのプログラム起動（startObjectPrograms）で引く。 */
  private readonly workspacesByName: ReadonlyMap<string, Blockly.Workspace>
  /**
   * 実行中に生成されたオブジェクト（クローン等）のハンドラ。プレイ毎にクリアする。
   * 配送・接触判定・破棄検知は静的ハンドラと合わせて allHandlers() で走査する。
   */
  private dynamicHandlers: ObjectHandlers[] = []
  /** 現在のプレイの開始時刻（sensing_timer の起点。クローンにも同じ起点を使う）。 */
  private sessionStartMs = 0
  /** objectId → オブジェクト名。接触ビヘイビアの逆引きに使う。 */
  private readonly idToName: ReadonlyMap<string, string>
  /**
   * ServerStorage 相当テンプレート本体のオブジェクト ID（プレイ毎に算出）。
   * 学習者プログラムの起動・配送から除外する（MissionBehaviors.
   * serverStorageTemplates 参照）。
   */
  private inertObjectIds: ReadonlySet<string> = new Set()
  private session: RunSession | null = null
  /** プレイ開始毎に factory から生成する環境ビヘイビア。未設定時は null。 */
  private behaviors: MissionBehaviors | null = null
  /** World 購読の解除関数（外部 destroy の検知用）。実行中のみ非 null。 */
  private unsubscribeWorld: (() => void) | null = null
  /** onTouchObject の周期チェックタイマー。実行中かつハンドラがあるときのみ非 null。 */
  private touchWatchTimer: ReturnType<typeof setInterval> | null = null
  /**
   * 現在接触中の selfId → 相手 stateId 集合。enter エッジ（非接触→接触）で
   * のみ発火するためのラッチ。プレイ開始毎にクリアする。
   */
  private readonly touchingPairs = new Map<string, Set<string>>()

  constructor(
    private readonly world: EditablePreviewWorld,
    descriptor: SceneDescriptor,
    workspaces: Map<string, Blockly.Workspace>,
    private readonly behaviorsFactory?: MissionBehaviorsFactory,
    private readonly options?: RuntimeOptions,
  ) {
    const nameToId = buildNameToId(descriptor)
    this.idToName = buildIdToName(descriptor)
    this.workspacesByName = new Map(workspaces)
    const handlers: ObjectHandlers[] = []

    for (const [name, workspace] of workspaces) {
      const objectId = nameToId.get(name)
      if (!objectId) {
        console.warn(
          `[preview] descriptor に存在しないオブジェクト名の workspace を無視: ${name}`,
        )
        continue
      }
      const interpreter = new Interpreter(world, objectId, {
        onOutput: (text) => this.options?.onOutput?.(text),
        onPlaySound: (id) => this.options?.onPlaySound?.(id),
      })
      handlers.push(buildHandlers(objectId, interpreter, workspace))
    }

    this.handlers = handlers
    this.objectIds = handlers.map((handler) => handler.objectId)
  }

  start(): void {
    if (this.session) {
      // 既に実行中なら一旦止めてから再スタートする（多重起動防止）。
      this.stop()
    }
    const session = new RunSession(this.objectIds)
    this.session = session

    // 環境ビヘイビアはプレイ毎に新規生成し、内部状態（isFall 等）を初期化する。
    this.behaviors = this.behaviorsFactory ? this.behaviorsFactory() : null

    // ServerStorage 相当テンプレート（実機では Script が実行されない）の
    // オブジェクト本体を、学習者プログラムの起動・配送から除外する。
    // クローンには startObjectPrograms 経由で起動する（Clone() の Script 複製）。
    const serverStorageNames = new Set(
      this.behaviors?.serverStorageTemplates ?? [],
    )
    this.inertObjectIds = new Set(
      this.handlers
        .filter((handler) =>
          serverStorageNames.has(this.idToName.get(handler.objectId) ?? ''),
        )
        .map((handler) => handler.objectId),
    )

    // Lua はプレイ毎にスクリプトを頭から実行する。各インタプリタの変数を初期化し、
    // タイマー起点を全オブジェクト共通の開始時刻に揃える。
    const startMs = performance.now()
    this.sessionStartMs = startMs
    this.dynamicHandlers = []
    for (const handler of this.handlers) {
      handler.interpreter.beginRun(startMs)
    }

    for (const handler of this.handlers) {
      if (this.inertObjectIds.has(handler.objectId)) {
        continue
      }
      const context = this.createContext(session, handler.objectId, 0)
      for (const substack of handler.onPlay) {
        void this.runHandler(handler, substack, context)
      }
    }

    // 環境ビヘイビアの onPlay を全体 signal で並行起動する。
    const behaviorContext = this.createBehaviorContext(session)
    for (const behavior of this.behaviors?.onPlay ?? []) {
      void this.runBehavior(behavior, behaviorContext)
    }

    // World からオブジェクトが消えたら（環境ビヘイビアの world.destroy 等、
    // control_destroy 以外の経路）、当該オブジェクトのハンドラも中断する。
    // 実機の Destroy() が配下 Script を止めるのと同じ挙動にする
    // （例: RJF05 で砲弾が命中したマトの forever ループが走り続けない）。
    this.unsubscribeWorld = this.world.subscribe((states) => {
      if (this.session !== session) {
        return
      }
      for (const handler of this.allHandlers()) {
        if (!states.has(handler.objectId)) {
          session.abortObject(handler.objectId)
        }
      }
    })

    this.startTouchObjectWatcher(session)
  }

  stop(): void {
    if (this.touchWatchTimer !== null) {
      clearInterval(this.touchWatchTimer)
      this.touchWatchTimer = null
    }
    this.touchingPairs.clear()
    if (this.unsubscribeWorld) {
      this.unsubscribeWorld()
      this.unsubscribeWorld = null
    }
    if (this.session) {
      this.session.abortAll()
      this.session = null
    }
    // クローン等のハンドラはプレイに紐づくので破棄する（abort は session 経由で済み）。
    this.dynamicHandlers = []
    // 環境ビヘイビアのループも全体 signal の abort で止まる。参照だけ落とす。
    this.behaviors = null
    // ミッション GUI の HUD はプレイに紐づく表示なので停止で消す。
    this.options?.onHudText?.(null)
  }

  reset(): void {
    this.stop()
    this.world.reset()
  }

  emitClick(objectId: string): void {
    // 環境ビヘイビア（onClick、キー=オブジェクト名）を Blockly ハンドラより
    // 先に発火する。Blockly 側の同期 control_destroy で対象が消えると、同じ
    // クリックの環境ビヘイビアが解決できなくなるため。
    this.fireNamedBehavior(objectId, this.behaviors?.onClick)
    // ClickDetector の MaxActivationDistance（既定 32 studs）相当の距離制限。
    // 本家はオブジェクトへ近づかないとクリックできない。
    if (!this.isWithinClickDistance(objectId)) {
      return
    }
    this.fireObjectHandlers(objectId, (handler) => handler.onClicked)
  }

  /**
   * アバターが対象のクリック受理距離内にいるか（アバター不在シーンは常に可）。
   * 距離は対象の中心ではなく**当たり判定ボックス（軸並行）の最近接点**まで測る。
   * 中心距離だと、おに（100×100 の壁で中心が地上 50 studs 上にある。RJS06）の
   * ような大きなパーツは、表面に密着しても中心が常に 32 studs 超になりクリック
   * できない。実機の MaxActivationDistance がパーツ表面までで測るのに揃える。
   */
  private isWithinClickDistance(objectId: string): boolean {
    const states = this.world.getState()
    const avatar = states.get(AVATAR_ID)
    const target = states.get(objectId)
    if (!avatar || !target) {
      return true
    }
    // 軸ごとに「中心からの距離 − 半サイズ」を 0 でクランプ（箱の内側は 0）。
    // rjs04 の sphereTouchesPart と同じ箱最近接の距離計算（回転は無視する近似）。
    const gapX = Math.max(
      Math.abs(avatar.position.x - target.position.x) - target.size.x / 2,
      0,
    )
    const gapY = Math.max(
      Math.abs(avatar.position.y - target.position.y) - target.size.y / 2,
      0,
    )
    const gapZ = Math.max(
      Math.abs(avatar.position.z - target.position.z) - target.size.z / 2,
      0,
    )
    return (
      gapX * gapX + gapY * gapY + gapZ * gapZ <=
      CLICK_MAX_ACTIVATION_DISTANCE * CLICK_MAX_ACTIVATION_DISTANCE
    )
  }

  /**
   * onTouchObject（「(名前)が触れたとき」）の周期ウォッチャーを開始する。
   * 固定 body 同士は物理エンジンの接触イベントが出ないため、World 状態の
   * AABB 重なりをポーリングして Touched を近似する。enter エッジでのみ発火。
   */
  private startTouchObjectWatcher(session: RunSession): void {
    // クローンのプログラム起動（startObjectPrograms）からも呼ばれるため、
    // 既に動作中なら二重起動しない。
    if (this.touchWatchTimer !== null) {
      return
    }
    if (!this.allHandlers().some((handler) => handler.onTouchObject.size > 0)) {
      return
    }
    this.touchingPairs.clear()
    this.touchWatchTimer = setInterval(() => {
      if (this.session !== session) {
        return
      }
      this.checkTouchObjectOverlaps(session)
    }, TOUCH_OBJECT_CHECK_INTERVAL_MS)
  }

  /** onTouchObject の重なりを 1 回分チェックし、新規接触ぶんを発火する。 */
  private checkTouchObjectOverlaps(session: RunSession): void {
    const states = this.world.getState()
    for (const handler of this.allHandlers()) {
      if (handler.onTouchObject.size === 0) {
        continue
      }
      const self = states.get(handler.objectId)
      // 消滅済み・CanTouch=false の self は判定しない（Roblox の Touched 同等）。
      if (!self || !self.canTouch) {
        continue
      }
      for (const [targetName, substacks] of handler.onTouchObject) {
        for (const state of states.values()) {
          if (state.id === self.id) {
            continue
          }
          if (!state.canTouch) {
            continue
          }
          if (!matchesTouchTargetName(state.name, targetName)) {
            continue
          }
          const touchedIds = this.touchingPairs.get(self.id) ?? new Set()
          if (!isAabbTouching(self, state)) {
            touchedIds.delete(state.id)
            continue
          }
          if (touchedIds.has(state.id)) {
            continue
          }
          touchedIds.add(state.id)
          this.touchingPairs.set(self.id, touchedIds)
          const context = this.createContext(session, handler.objectId, 0)
          for (const substack of substacks) {
            void this.runHandler(handler, substack, context)
          }
        }
      }
    }
  }

  emitTouch(objectId: string): void {
    // onClick と同じ理由で環境ビヘイビアを先に発火する。
    this.fireNamedBehavior(objectId, this.behaviors?.onTouchCharacter)
    this.fireObjectHandlers(objectId, (handler) => handler.onTouchCharacter)
  }

  dispose(): void {
    this.stop()
  }

  /**
   * 指定オブジェクト宛のハンドラ（クリック/接触）を発火する。
   * 実行中（session あり）かつ未 destroy のオブジェクトに対してのみ起動する。
   * onPlay/onMessage と同じ非同期・中断・連鎖防止モデルに乗せる。
   *
   * @param objectId 発火対象のオブジェクト ID。
   * @param select handler から実行対象の substack 群を取り出す関数。
   */
  private fireObjectHandlers(
    objectId: string,
    select: (handler: ObjectHandlers) => ReadonlyArray<Blockly.Block | null>,
  ): void {
    const session = this.session
    if (!session) {
      return
    }
    // destroy 済みオブジェクトのハンドラは発火しない。
    if (!this.world.exists(objectId)) {
      return
    }
    for (const handler of this.allHandlers()) {
      if (handler.objectId !== objectId) {
        continue
      }
      if (this.inertObjectIds.has(objectId)) {
        continue
      }
      const context = this.createContext(session, objectId, 0)
      for (const substack of select(handler)) {
        void this.runHandler(handler, substack, context)
      }
    }
  }

  /**
   * 実行コンテキストを作る。
   * - signal は objectId 単位（destroy で個別中断できるよう）。
   * - dispatchMessage は連鎖深さを 1 増やして再帰的に配送する。
   * - destroySelf は当該オブジェクトの全ハンドラを中断する。
   */
  private createContext(
    session: RunSession,
    objectId: string,
    depth: number,
  ): ExecutionContext {
    return {
      signal: session.signalFor(objectId),
      dispatchMessage: (message: string) =>
        this.dispatchMessage(message, session, depth),
      destroySelf: () => session.abortObject(objectId),
      startObjectPrograms: (cloneId: string, templateName: string) => {
        // 停止後の遅延コールバックが新プレイへプログラムを起動しないようにする。
        if (this.session === session) {
          this.startObjectPrograms(cloneId, templateName)
        }
      },
      // 停止後・別プレイの interpreter からのクローン生成は許可しない（false）。
      tryConsumeCloneBudget: () =>
        this.session === session && session.tryConsumeDynamicClone(),
      // RunSession の共有クロックに揃える（環境ビヘイビアの wait と同じ起点）。
      wait: (seconds: number) =>
        session.wait(seconds, session.signalFor(objectId)),
    }
  }

  /**
   * メッセージを全オブジェクト横断で配送し、該当する onMessage ハンドラを発火する。
   * 深さガードを超えたら警告して打ち切る（無限連鎖の防止）。
   */
  private dispatchMessage(
    message: string,
    session: RunSession,
    depth: number,
  ): void {
    // 配送元セッションが既に止まっていれば何もしない。
    if (session !== this.session) {
      return
    }
    if (depth >= MAX_MESSAGE_DEPTH) {
      console.warn(
        `[preview] メッセージ連鎖が深すぎます（${message}）。配送を打ち切ります。`,
      )
      return
    }
    for (const handler of this.allHandlers()) {
      // destroy 済みオブジェクトの onMessage は発火しない。
      if (!this.world.exists(handler.objectId)) {
        continue
      }
      // ServerStorage 相当テンプレート本体は受信もしない（Script 非実行）。
      if (this.inertObjectIds.has(handler.objectId)) {
        continue
      }
      const substacks = handler.onMessage.get(message)
      if (!substacks) {
        continue
      }
      const context = this.createContext(session, handler.objectId, depth + 1)
      for (const substack of substacks) {
        void this.runHandler(handler, substack, context)
      }
    }

    // 環境ビヘイビアの onMessage も発火する（深さガードの対象外でよい。
    // ビヘイビアは sendMessage を呼ばないため連鎖しない）。
    const behavior = this.behaviors?.onMessage?.get(message)
    if (behavior) {
      void this.runBehavior(behavior, this.createBehaviorContext(session))
    }
  }

  /**
   * 1 ハンドラを実行する。例外は握り潰さず、実行を止めないようログに残す。
   */
  private async runHandler(
    handler: ObjectHandlers,
    substack: Blockly.Block | null,
    context: ExecutionContext,
  ): Promise<void> {
    try {
      await handler.interpreter.run(substack, context)
    } catch (error) {
      console.error(
        `[preview] ハンドラ実行中にエラー (object=${handler.objectId})`,
        error,
      )
    }
  }

  /** 静的（descriptor 由来）+ 動的（クローン等）の全ハンドラ。 */
  private allHandlers(): ReadonlyArray<ObjectHandlers> {
    return this.dynamicHandlers.length === 0 ?
        this.handlers
      : [...this.handlers, ...this.dynamicHandlers]
  }

  /**
   * 実行中に生成されたオブジェクト（環境ビヘイビアのクローン等）へ、テンプレート
   * 名に学習者が付けたプログラムを起動する。Roblox の Clone() が配下の Script ごと
   * 複製され、workspace へ親付けされた時点で実行が始まるのと同じ挙動を再現する
   * （例: RJF05 の CreateMatoScript が生成する ③マト のクローン）。
   * テンプレート名にプログラムが無ければ何もしない。
   */
  private startObjectPrograms(objectId: string, templateName: string): void {
    const session = this.session
    if (!session || !this.world.exists(objectId)) {
      return
    }
    const workspace = this.workspacesByName.get(templateName)
    if (!workspace) {
      return
    }
    // 自己複製プログラム（onPlay → 自分を control_clone）の無限増殖ガード。
    if (!session.tryConsumeDynamicProgramStart()) {
      console.warn(
        `[preview] クローンへのプログラム起動が上限（${MAX_DYNAMIC_PROGRAM_STARTS}）に達しました: ${templateName}`,
      )
      return
    }
    // onPlay の起動は現在のコールスタックから切り離す。runHandler は最初の
    // await まで同期実行されるため、クローンの onPlay が同期的に control_clone
    // を再帰してスタックを深くしないようにする。
    setTimeout(() => {
      if (this.session !== session || !this.world.exists(objectId)) {
        return
      }
      this.startObjectProgramsNow(session, objectId, workspace)
    }, 0)
  }

  /** startObjectPrograms の実体（予算消費・遅延ディスパッチ後）。 */
  private startObjectProgramsNow(
    session: RunSession,
    objectId: string,
    workspace: Blockly.Workspace,
  ): void {
    const interpreter = new Interpreter(this.world, objectId, {
      onOutput: (text) => this.options?.onOutput?.(text),
      onPlaySound: (id) => this.options?.onPlaySound?.(id),
    })
    // タイマー起点（sensing_timer = Lua time()）はサーバー共通なので、
    // プレイ開始時刻に揃える（クローン生成時刻ではない）。
    interpreter.beginRun(this.sessionStartMs)
    const handlers = buildHandlers(objectId, interpreter, workspace)
    session.registerObject(objectId)
    this.dynamicHandlers.push(handlers)
    // クローンのプログラムが onTouchObject を持つ場合に備えてウォッチャーを
    // 起動する（既に動作中なら no-op）。
    if (handlers.onTouchObject.size > 0) {
      this.startTouchObjectWatcher(session)
    }
    const context = this.createContext(session, objectId, 0)
    for (const substack of handlers.onPlay) {
      void this.runHandler(handlers, substack, context)
    }
  }

  /**
   * objectId のオブジェクト名で環境ビヘイビアのマップ（onTouchCharacter /
   * onClick）を引き、あれば発火する。実行中（session あり）かつ未 destroy の
   * ときのみ起動する。
   */
  private fireNamedBehavior(
    objectId: string,
    behaviorsByName: ReadonlyMap<string, BehaviorHandler> | undefined,
  ): void {
    const session = this.session
    if (!session || !behaviorsByName) {
      return
    }
    if (!this.world.exists(objectId)) {
      return
    }
    // 実行時生成オブジェクト（ビヘイビアの addObject / clone）は初期 descriptor の
    // idToName に載らないため、現在の World 状態から名前を引く（例: RJF06 の
    // ④ボスのこうげき。固定 Map だけだと生成された炎の球の接触が発火しない）。
    const name =
      this.idToName.get(objectId) ?? this.world.getState().get(objectId)?.name
    if (!name) {
      return
    }
    const behavior = behaviorsByName.get(name)
    if (behavior) {
      void this.runBehavior(behavior, this.createBehaviorContext(session))
    }
  }

  /**
   * 環境ビヘイビア用の実行コンテキストを作る。
   * signal はプレイ全体の signal（stop() で abort）。wait は中断可能な待機。
   */
  private createBehaviorContext(session: RunSession): BehaviorContext {
    const signal = session.rootSignal
    return {
      world: this.world,
      signal,
      // white（インタプリタ）と同じ RunSession 共有クロックに揃え、両者が
      // 個別 setTimeout チェーンの積み重ね誤差で相対的にずれるのを防ぐ。
      wait: (seconds: number) => session.wait(seconds, signal),
      setHudText:
        this.options?.onHudText ?
          (text: string | null, style?: HudStyle, slot?: string) => {
            // 停止後の遅延コールバックが HUD を書き戻さないようにする。
            if (this.session === session) {
              this.options?.onHudText?.(text, style, slot)
            }
          }
        : undefined,
      setBgm:
        this.options?.onBgm ?
          (url: string) => {
            // 停止後の遅延コールバックが BGM を鳴らし直さないようにする。
            if (this.session === session) {
              this.options?.onBgm?.(url)
            }
          }
        : undefined,
      startObjectPrograms: (objectId: string, templateName: string) => {
        // 停止後の遅延コールバックが新プレイへプログラムを起動しないようにする。
        if (this.session === session) {
          this.startObjectPrograms(objectId, templateName)
        }
      },
    }
  }

  /**
   * 環境ビヘイビアを 1 つ実行する。例外は握り潰さずログに残し、他の実行を止めない
   * （runHandler と同じ方針）。
   */
  private async runBehavior(
    behavior: BehaviorHandler,
    context: BehaviorContext,
  ): Promise<void> {
    try {
      await behavior(context)
    } catch (error) {
      console.error('[preview] 環境ビヘイビア実行中にエラー', error)
    }
  }
}

/**
 * おためしプレイ実行ランタイムを生成する。
 *
 * @param descriptor シーン記述。name → id の解決に使う。
 * @param world 見た目・空間状態を保持する World。インタプリタが mutation する。
 * @param workspaces オブジェクト名（descriptor.objects[].name）→ その Blockly workspace。
 * @param behaviorsFactory ミッション環境ビヘイビアのファクトリ（省略可）。プレイ毎に
 *   新規生成し、テンプレート rbxl のギミック（環境スクリプト）を再現する。
 */
export const createRuntime = (
  descriptor: SceneDescriptor,
  world: EditablePreviewWorld,
  workspaces: Map<string, Blockly.Workspace>,
  behaviorsFactory?: MissionBehaviorsFactory,
  options?: RuntimeOptions,
): PreviewRuntime =>
  new SceneRuntime(world, descriptor, workspaces, behaviorsFactory, options)
